import { useCallback, useState } from 'react'
import {
  ApiError,
  fetchLiveness,
  fetchReadiness,
  fetchRoot,
  joinUrl,
  toApiError,
} from '@/lib/api'
import { fetchHealth } from '@/lib/api'
import type { ApiContext } from '@/lib/api'
import { useApiContext } from '@/lib/hooks/useApiContext'

export type ProbeStatus = 'idle' | 'running' | 'pass' | 'fail'

export interface ProbeResult {
  id: string
  label: string
  path: string
  description: string
  status: ProbeStatus
  durationMs?: number
  detail?: string
  error?: string
}

interface ProbeDefinition {
  id: string
  label: string
  path: string
  description: string
  run: (context: ApiContext, signal: AbortSignal) => Promise<string>
}

/**
 * Read-only probes against the endpoints the backend already exposes. This
 * deliberately never POSTs a query — a diagnostics page that runs the LLM
 * pipeline would cost real tokens every time someone opened it.
 */
const PROBES: ProbeDefinition[] = [
  {
    id: 'root',
    label: 'Service root',
    path: '/',
    description: 'Confirms something is listening and reports the app identity.',
    run: async (context, signal) => {
      const root = await fetchRoot(context, signal)
      return `${root.application} v${root.version} · ${root.environment} · ${root.status}`
    },
  },
  {
    id: 'live',
    label: 'Liveness',
    path: '/health/live',
    description: 'The process is up and serving requests.',
    run: async (context, signal) => {
      const live = await fetchLiveness(context, signal)
      return live.status
    },
  },
  {
    id: 'health',
    label: 'Health',
    path: '/health',
    description: 'Application name, version, and environment.',
    run: async (context, signal) => {
      const health = await fetchHealth(context, signal)
      return `${health.status} · ${health.application} v${health.version} · ${health.environment}`
    },
  },
  {
    id: 'ready',
    label: 'Readiness',
    path: '/health/ready',
    description:
      'Currently a static response — the backend has not yet wired real dependency checks into it.',
    run: async (context, signal) => {
      const ready = await fetchReadiness(context, signal)
      return ready.status
    },
  },
]

function initialResults(): ProbeResult[] {
  return PROBES.map(({ id, label, path, description }) => ({
    id,
    label,
    path,
    description,
    status: 'idle' as ProbeStatus,
  }))
}

export interface DiagnosticsState {
  results: ProbeResult[]
  isRunning: boolean
  run: () => Promise<void>
  resolvedUrls: Record<string, string>
}

export function useDiagnostics(): DiagnosticsState {
  const context = useApiContext()
  const [results, setResults] = useState<ProbeResult[]>(initialResults)
  const [isRunning, setIsRunning] = useState(false)

  const run = useCallback(async () => {
    setIsRunning(true)
    setResults((current) =>
      current.map((result) => ({ ...result, status: 'running' as ProbeStatus })),
    )

    const controller = new AbortController()

    // Sequential rather than parallel: four concurrent probes against a single
    // uvicorn worker can queue behind each other and make a healthy server look
    // slow, which is exactly the signal this page is meant to report on.
    for (const probe of PROBES) {
      const startedAt = performance.now()

      try {
        const detail = await probe.run(context, controller.signal)
        const durationMs = performance.now() - startedAt

        setResults((current) =>
          current.map((result) =>
            result.id === probe.id
              ? { ...result, status: 'pass', detail, durationMs }
              : result,
          ),
        )
      } catch (error) {
        const apiError =
          error instanceof ApiError ? error : toApiError(error, 'Probe failed.')
        const durationMs = performance.now() - startedAt

        setResults((current) =>
          current.map((result) =>
            result.id === probe.id
              ? {
                  ...result,
                  status: 'fail',
                  error: apiError.message,
                  durationMs,
                }
              : result,
          ),
        )
      }
    }

    setIsRunning(false)
  }, [context])

  const resolvedUrls = Object.fromEntries(
    PROBES.map((probe) => [
      probe.id,
      context.baseUrl.trim() ? joinUrl(context.baseUrl, probe.path) : probe.path,
    ]),
  )

  return { results, isRunning, run, resolvedUrls }
}
